<template>
    <div class="ai-review-page">
        <ai-meeting-review v-if="job && job.nodeType === 'meeting'" :job-id="job.id" />
        <!-- PROCESSING -->
        <div v-else-if="state == 'processing'" class="ai-review-card">
            <div class="ai-review-orb"></div>
            <h2>Your {{ typeLabel.toLowerCase() }} draft is still being generated</h2>
            <p v-if="job && job.stage">{{ job.stage }}...</p>
            <p>This page will update automatically — or come back later using the link in your email.</p>
        </div>

        <!-- ERROR / ACCESS -->
        <div v-else-if="state == 'error'" class="ai-review-card">
            <h2>Something went wrong</h2>
            <p>{{ errorMessage }}</p>
            <router-link to="/playbook/home">Back to Ironbook</router-link>
        </div>

        <!-- HAND-OFF: a recording sent to someone to build (no draft yet) -->
        <div v-else-if="state == 'handoff'" class="ai-review-card ai-handoff-card">
            <h2>{{ job.handoffToMe ? 'A screen recording from ' + (job.handoffFrom || 'a colleague') : 'Your screen recording, sent to ' + (job.handoffTo || 'a colleague') }}</h2>
            <p class="ai-handoff-when">Recorded {{ recordedWhen }}<template v-if="!job.handoffToMe"> · waiting for {{ job.handoffTo || 'them' }} to build it</template></p>
            <blockquote v-if="job.note" class="ai-handoff-note">{{ job.note }}</blockquote>
            <template v-if="job.recordingUrl">
                <video class="ai-handoff-video" controls playsinline preload="metadata" :src="recordingUrl"></video>
                <!-- Keelan, 27 Oct 2026: what the recording builds, and whether the video is kept with it -->
                <div class="ai-handoff-kind">
                    <span class="ai-handoff-kind-label">Build a</span>
                    <span v-for="t in buildTypes" :key="t.key" class="ai-handoff-kind-pill" :class="{ 'ai-handoff-kind-on': buildType == t.key }" @click="buildType = t.key">{{ t.label }}</span>
                </div>
                <label class="ai-handoff-save">
                    <input type="checkbox" v-model="saveVideo" />
                    <span class="ai-handoff-switch" aria-hidden="true"></span>
                    <span><strong>Save the video with it</strong><em>{{ saveVideo ? 'The recording is added to Videos next to the new ' + buildNoun + '.' : 'The recording is only used to write the draft, then deleted.' }}</em></span>
                </label>
                <textarea v-if="job.handoffToMe" v-model="buildNote" rows="3" class="ai-handoff-build-note" :placeholder="'Anything the Forge should know before it writes the ' + buildNoun + ' (optional)'"></textarea>
                <div class="ai-handoff-actions">
                    <div class="ai-btn" :class="{ 'disable-button': acting }" @click="build()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                        {{ acting == 'build' ? 'Starting…' : 'Build the ' + buildNoun + ' with the Forge' }}
                    </div>
                    <div class="ai-btn ai-btn-ghost" :class="{ 'disable-button': acting }" @click="discard()">{{ acting == 'discard' ? 'Discarding…' : 'Discard the recording' }}</div>
                </div>
                <p class="ai-handoff-fine">Build sends the recording to the Forge under your name. The draft comes back here for you to review<template v-if="saveVideo">, and the recording is kept as the {{ buildNoun }}'s video</template>.</p>
            </template>
            <template v-else>
                <div class="ai-review-orb"></div>
                <p>Saving the recording… this page updates on its own.</p>
            </template>
            <div v-if="actionError" class="ai-handoff-error">{{ actionError }}</div>
        </div>

        <!-- DISCARDED hand-off -->
        <div v-else-if="state == 'discarded'" class="ai-review-card">
            <h2>This recording was discarded</h2>
            <p>Nothing was built from it. Record it again from the Forge if it is still needed.</p>
            <router-link to="/ai-hub">Back to the Forge</router-link>
        </div>

        <!-- CONSUMED -->
        <div v-else-if="state == 'consumed'" class="ai-review-card">
            <h2>{{ job && job.targetSlug ? 'This update was already applied' : 'This draft was already turned into a ' + typeLabel.toLowerCase() }}</h2>
            <p v-if="job && job.createdSlug">You can keep working on it in the editor.</p>
            <router-link v-if="job && job.createdSlug" :to="editRoutes[nodeType] + job.createdSlug">Open the {{ typeLabel.toLowerCase() }}</router-link>
            <router-link v-else to="/playbook/home">Back to Ironbook</router-link>
            <router-link v-if="job && job.videoSlug" :to="'/playbook/' + job.videoSlug" style="margin-left:16px">Watch the video</router-link>
        </div>

        <!-- COMPLETE — review modal -->
        <template v-else-if="state == 'complete'">
            <div class="ai-review-card ai-review-bg">
                <h2>AI Draft Review</h2>
                <p v-if="job.targetSlug && job.mode == 'extend'">These additions go onto <strong>"{{ job.targetName || job.targetSlug }}"</strong> — accepting saves them as a draft awaiting approval.</p>
                <p v-else-if="job.targetSlug">This draft <strong>updates "{{ job.targetName || job.targetSlug }}"</strong> — accepting saves it onto that process as a draft awaiting approval.</p>
                <p v-else>Review the generated {{ typeLabel }} below, adjust the details the AI filled in, and create it.</p>
            </div>
            <ai-process-modal
                mode="generate"
                :node-type="job.nodeType || 'process'"
                :create-mode="!job.targetSlug"
                :busy="creating"
                :accept-label="job.targetSlug ? 'Save Update as Draft' : 'Create ' + typeLabel"
                :initial-draft="job.draft"
                :initial-note="job.note"
                :job-id="job.id"
                @accept="onAccept"
                @close="$router.push('/playbook/home')"
            />
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIProcessModal from '@/components/AIProcessModal.vue';
import AIMeetingReview from '@/components/AIMeetingReview.vue';
import { createNodeFromDraft, metaFromDraft, EDIT_ROUTES, AINodeType } from '@/ai/createNodeFromDraft';

// Resume-review screen for background capture→process jobs: the emailed
// "review your draft" link lands here. Hosts the same AIProcessModal review
// experience (inline editing + request-changes loop + the create-mode details
// panel); Accept creates the node through the shared quick-create helper and
// routes to the editor.
export default defineComponent({
    name: 'AIReviewDraft',
    components: { AiMeetingReview: AIMeetingReview, AiProcessModal: AIProcessModal },
    data() {
        return {
            state: 'processing' as string, // processing | complete | error | consumed | handoff | discarded
            job: null as any,
            errorMessage: '',
            pollTimer: null as any,
            creating: false,
            buildNote: '',          // hand-off: what the builder tells the Forge (defaults to the sender's note)
            buildType: 'process',   // hand-off: what it builds (defaults to the sender's choice)
            saveVideo: true,        // hand-off: keep the recording as a Video node
            editRoutes: EDIT_ROUTES,
            buildTypes: [
                { key: 'process', label: 'Process' },
                { key: 'sheet', label: 'Role sheet' },
                { key: 'timeline', label: 'Value engine' },
                { key: 'checklist', label: 'Powerlist' }
            ],
            acting: '' as string,   // hand-off: 'build' | 'discard' while the request runs
            actionError: ''
        }
    },
    computed: {
        recordingUrl(): string {
            const url = this.job && this.job.recordingUrl;
            return url ? (this.$store.state.apiUrl || '') + url : '';
        },
        recordedWhen(): string {
            const when = this.job && this.job.created;
            return when ? new Date(when).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
        },
        nodeType(): AINodeType {
            const t = this.job && this.job.nodeType;
            return (['process', 'checklist', 'sheet', 'timeline'].includes(t) ? t : 'process') as AINodeType;
        },
        typeLabel(): string {
            return this.nodeType == 'checklist' ? 'Powerlist' : this.nodeType == 'sheet' ? 'Role Sheet' : this.nodeType == 'timeline' ? 'Value Engine' : 'Process';
        },
        buildNoun(): string {
            return (this.buildTypes.find((t: any) => t.key == this.buildType) || this.buildTypes[0]).label.toLowerCase();
        }
    },
    created() {
        this.$store.state.routeLoaded = true;
        this.fetchJob();
    },
    unmounted() {
        this.stopPolling();
    },
    methods: {
        stopPolling() {
            if (this.pollTimer) clearInterval(this.pollTimer);
            this.pollTimer = null;
        },
        async sharedMeeting(): Promise<boolean> {
            try {
                const id = Number(this.$route.params.jobId);
                await axios.get(`${this.$store.state.apiUrl}/api/ai/meeting-notes/${id}`);
                this.job = { id, nodeType: 'meeting' };
                return true;
            } catch { return false; }
        },
        async fetchJob() {
            await axios.get(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.$route.params.jobId}`)
                .then(resp => {
                    this.job = resp.data;
                    if (resp.data.nodeType === 'meeting') { this.stopPolling(); return; }
                    if (resp.data.status === 'complete') {
                        this.stopPolling();
                        this.state = 'complete';
                    } else if (resp.data.status === 'consumed') {
                        this.stopPolling();
                        this.state = 'consumed';
                    } else if (resp.data.status === 'error') {
                        this.stopPolling();
                        this.errorMessage = resp.data.error || 'This draft could not be generated.';
                        this.state = 'error';
                    } else if (resp.data.status === 'handoff') {
                        // Waiting for the builder; keep polling only while the recording is still being saved
                        if (this.state !== 'handoff') {
                            this.buildNote = resp.data.note || '';
                            this.buildType = this.buildTypes.some((t: any) => t.key == resp.data.nodeType) ? resp.data.nodeType : 'process';
                        }
                        this.state = 'handoff';
                        if (resp.data.recordingUrl) this.stopPolling();
                        else if (!this.pollTimer) this.pollTimer = setInterval(() => this.fetchJob(), 2500);
                    } else if (resp.data.status === 'discarded') {
                        this.stopPolling();
                        this.state = 'discarded';
                    } else {
                        this.state = 'processing';
                        if (!this.pollTimer) this.pollTimer = setInterval(() => this.fetchJob(), 2500);
                    }
                })
                .catch(async e => {
                    this.stopPolling();
                    // A meeting recording shared with this person opens read-only even
                    // though they can't see the capture job itself.
                    if ([403, 404].includes(e?.response?.status) && await this.sharedMeeting()) return;
                    if (e?.response?.status === 403) {
                        this.errorMessage = 'You need editor access to review AI drafts — ask your administrator.';
                    } else if (e?.response?.status === 404) {
                        this.errorMessage = 'This draft could not be found.';
                    } else {
                        this.errorMessage = 'Could not load this draft — please try again.';
                    }
                    this.state = 'error';
                });
        },
        // Hand-off: run the Forge on the saved recording. The job turns into an ordinary
        // processing job of this person, and the normal polling takes over.
        async build() {
            if (this.acting || !this.job) return;
            this.acting = 'build';
            this.actionError = '';
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.job.id}/build`, { note: this.buildNote, nodeType: this.buildType, saveVideo: this.saveVideo });
                this.state = 'processing';
                this.job = { ...this.job, status: 'processing', stage: 'Reading the recording', nodeType: this.buildType };
                if (!this.pollTimer) this.pollTimer = setInterval(() => this.fetchJob(), 2500);
            } catch (e: any) {
                this.actionError = (e?.response && typeof e.response.data === 'string' && e.response.data.length < 300 && e.response.data) || 'The Forge could not start on this recording — please try again.';
            } finally {
                this.acting = '';
            }
        },
        async discard() {
            if (this.acting || !this.job) return;
            if (!confirm('Discard this recording? It will be deleted and nothing will be built from it.')) return;
            this.acting = 'discard';
            this.actionError = '';
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.job.id}/discard`, {});
                this.stopPolling();
                this.state = 'discarded';
            } catch (e: any) {
                this.actionError = (e?.response && typeof e.response.data === 'string' && e.response.data.length < 300 && e.response.data) || 'The recording could not be discarded — please try again.';
            } finally {
                this.acting = '';
            }
        },
        async onAccept(draft: any, jobId: any, meta: any, videoTitle?: string | null) {
            if (this.creating) return;
            this.creating = true;
            if (this.job.targetSlug) {
                // Update / extend job: the server writes the (possibly inline-edited)
                // draft onto the existing node as a NeedsApproval draft and consumes the job.
                await axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.job.id}/apply`, { draft, videoTitle: videoTitle || null })
                    .then(() => {
                        this.$store.dispatch('setStoreData');
                        this.$router.push(EDIT_ROUTES[this.nodeType] + this.job.targetSlug);
                    })
                    .catch(e => {
                        if (e?.response?.status === 403) {
                            this.$router.push('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
                            return;
                        }
                        alert(e?.response?.data || 'Could not apply the update — please try again');
                        this.creating = false;
                    });
                return;
            }
            try {
                const slug = await createNodeFromDraft({
                    apiUrl: this.$store.state.apiUrl,
                    nodeType: this.nodeType,
                    draft,
                    meta: meta || metaFromDraft(draft, this.$store.state.user),
                    jobId: this.job.id
                });
                this.$store.dispatch('setStoreData');
                this.$router.push(EDIT_ROUTES[this.nodeType] + slug);
            } catch (e: any) {
                if (e?.status === 403) {
                    this.$router.push('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
                    return;
                }
                alert(e?.message || 'There was an error creating the process');
                this.creating = false;
            }
        }
    }
});
</script>

<style scoped>
.ai-review-page { min-height: 70vh; padding: 40px 20px; }
.ai-review-card {
    max-width: 560px; margin: 40px auto; text-align: center;
    background: #fff; border: 1px solid #f6e3d3; border-radius: 16px; padding: 36px 32px;
    box-shadow: 0 10px 40px rgba(70, 35, 10, .08);
}
.ai-review-card h2 { margin: 0 0 10px; color: #1f1712; }
.ai-review-card p { color: #6f655c; margin: 0 0 18px; }
.ai-review-card a { color: var(--ai-accent-deep); text-decoration: underline; }
.ai-review-bg { margin-bottom: 0; }
.ai-handoff-card { max-width: 760px; text-align: left; }
.ai-handoff-card h2 { font-size: 20px; }
.ai-handoff-when { font-size: 13px; color: #8a7f75; margin-bottom: 12px; }
.ai-handoff-note { margin: 0 0 14px; padding: 10px 14px; border-left: 3px solid #f0c9a8; background: #fff8f1; color: #4a3728; font-size: 14px; line-height: 1.5; white-space: pre-wrap; }
.ai-handoff-video { display: block; width: 100%; max-height: 420px; background: #000; border-radius: 10px; margin-bottom: 14px; }
.ai-handoff-build-note { width: 100%; box-sizing: border-box; margin: 0 0 12px; font-size: 14px; }
.ai-handoff-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.ai-handoff-fine { font-size: 12.5px; color: #8a7f75; margin: 12px 0 0; }
.ai-handoff-kind { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: 0 0 10px; }
.ai-handoff-kind-label { font-size: 13px; font-weight: 700; color: #4a3728; margin-right: 4px; }
.ai-handoff-kind-pill { cursor: pointer; font-size: 13px; padding: 5px 11px; border-radius: 999px; border: 1px solid #f0dcc8; background: #fff; color: #4a3728; }
.ai-handoff-kind-pill:hover { border-color: var(--ai-accent); }
.ai-handoff-kind-on { background: var(--ai-accent); border-color: var(--ai-accent); color: #fff; }
.ai-handoff-save { position: relative; display: flex; align-items: flex-start; gap: 10px; margin: 0 0 12px; font-size: 13.5px; color: #4a3728; line-height: 1.4; cursor: pointer; }
.ai-handoff-save input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.ai-handoff-switch { flex: none; position: relative; width: 34px; height: 20px; border-radius: 10px; background: #d8cfc6; transition: background .15s ease; margin-top: 1px; }
.ai-handoff-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform .15s ease; }
.ai-handoff-save input:checked + .ai-handoff-switch { background: var(--ai-accent); }
.ai-handoff-save input:checked + .ai-handoff-switch::after { transform: translateX(14px); }
.ai-handoff-save input:focus-visible + .ai-handoff-switch { outline: 2px solid var(--ai-accent); outline-offset: 2px; }
.ai-handoff-save em { display: block; font-style: normal; font-size: 12.5px; color: #6f655c; }
.ai-handoff-error { background: #fdecea; border: 1px solid #f5c6c3; color: #9c2b23; padding: 10px 14px; border-radius: 10px; margin-top: 12px; font-size: 13.5px; }
.ai-handoff-card .ai-review-orb { margin: 20px auto 14px; }
.ai-review-orb {
    width: 46px; height: 46px; border-radius: 50%; margin: 0 auto 18px;
    background: conic-gradient(from 0deg, #f59e0b, #f97316, #ea580c, #f59e0b);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
    animation: ai-review-spin 1.1s linear infinite;
}
@keyframes ai-review-spin { to { transform: rotate(360deg); } }
</style>
